/**
 * Renders cards from AI response: title, description, metadata, action buttons.
 * Uses Bootstrap Card and spacing consistent with WizardCreation / AllEnquiries cards.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Button } from "react-bootstrap";

const CARD_STYLE = {
  borderRadius: "16px",
  border: "1px solid #e9ecef",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
  transition: "transform 0.2s, box-shadow 0.2s",
};

function AIResponseCards({
  title,
  cards = [],
  onAction,
}) {
  const navigate = useNavigate();

  const handleAction = (action, card) => {
    if (action.action === "open_enquiry" && action.id != null) {
      navigate("/enquiry_profile", { state: { enquiry: card } });
    } else if (action.action === "edit_enquiry" && action.id != null) {
      navigate("/edit-enquiry", { state: { enquiry: card } });
    } else if (typeof onAction === "function") {
      onAction(action, card);
    } else if (typeof action.onClick === "function") {
      action.onClick(card);
    }
  };

  if (!Array.isArray(cards) || cards.length === 0) {
    return null;
  }

  return (
    <div className="mb-2">
      {title && (
        <h6 className="mb-2 fw-bold text-dark" style={{ fontSize: "1rem" }}>
          {title}
        </h6>
      )}
      <Row xs={1} sm={2} lg={3} className="g-2">
        {cards.map((card, idx) => {
          const cardTitle = card.title ?? card.name ?? card.first_name ?? `Item ${idx + 1}`;
          const description = card.description ?? card.subtitle ?? "";
          const metadata = card.metadata ?? card.fields ?? [];
          const actions = card.actions ?? [];
          return (
            <Col key={card.id ?? idx}>
              <Card
                className="h-100"
                style={CARD_STYLE}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 12px 28px rgba(0, 0, 0, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.02)";
                }}
              >
                <Card.Header style={{ backgroundColor: "#f9fafc", borderBottom: "1px solid #e9ecef" }} className="py-2">
                  <strong className="text-dark">{cardTitle}</strong>
                  {card.subheader && (
                    <small className="d-block text-muted">{card.subheader}</small>
                  )}
                </Card.Header>
                <Card.Body className="py-2">
                  {description && <p className="small text-muted mb-2">{description}</p>}
                  {Array.isArray(metadata) && metadata.length > 0 && (
                    <div className="d-flex flex-column gap-1 mb-2">
                      {metadata.map((m, i) => (
                        <div key={i} className="small">
                          {typeof m === "object" && m.label != null ? (
                            <>
                              <strong>{m.label}:</strong> {m.value ?? "—"}
                            </>
                          ) : (
                            String(m)
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {actions.length > 0 && (
                    <div className="d-flex flex-wrap gap-1">
                      {actions.map((action, aIdx) => (
                        <Button
                          key={aIdx}
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAction(action, card)}
                        >
                          {action.label || action.action}
                        </Button>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

export default AIResponseCards;
